// @ts-nocheck
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { QRCodeSVG } from 'qrcode.react';
import { Globe, Link2, QrCode, Copy, Check, Eye } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface TeamSharingControlsProps {
  teamId: string;
  teamName: string;
  isPublic: boolean;
  slug: string;
  viewCount: number;
  onUpdate: () => void;
}

const TeamSharingControls: React.FC<TeamSharingControlsProps> = ({
  teamId,
  teamName,
  isPublic,
  slug,
  viewCount,
  onUpdate
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);

  const publicUrl = `${window.location.origin}/team/${slug}`;

  const handleTogglePublic = async (newValue: boolean) => {
    try {
      setIsUpdating(true);
      const { error } = await supabase
        .from('coach_teams')
        .update({ is_public: newValue })
        .eq('id', teamId);

      if (error) throw error;

      toast.success(newValue ? 'Team is now public' : 'Team is now private');
      onUpdate();
    } catch (error) {
      console.error('Error updating team visibility:', error);
      toast.error('Failed to update team visibility');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const downloadQRCode = () => {
    const svg = document.getElementById('team-qr-code');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');

      const downloadLink = document.createElement('a');
      downloadLink.download = `${slug}-qr-code.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="h-5 w-5" />
          Public Sharing
        </CardTitle>
        <CardDescription>
          Share your team roster with parents, athletes, and recruiters
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Public Toggle */}
        <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
          <div className="space-y-0.5">
            <Label htmlFor="public-toggle" className="text-base font-medium">
              Make team public
            </Label>
            <p className="text-sm text-muted-foreground">
              Allow anyone with the link to view this team
            </p>
          </div>
          <Switch
            id="public-toggle"
            checked={isPublic}
            onCheckedChange={handleTogglePublic}
            disabled={isUpdating}
          />
        </div>

        {/* View Count */}
        {isPublic && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Eye className="h-4 w-4" />
            <span>{viewCount} {viewCount === 1 ? 'view' : 'views'}</span>
          </div>
        )}

        {/* Share Link */}
        {isPublic && (
          <>
            <div className="space-y-2">
              <Label>Share Link</Label>
              <div className="flex gap-2">
                <Input
                  value={publicUrl}
                  readOnly
                  className="flex-1"
                />
                <Button
                  onClick={handleCopyLink}
                  variant="outline"
                  size="icon"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* QR Code */}
            <div className="space-y-2">
              <Button
                onClick={() => setShowQR(!showQR)}
                variant="outline"
                className="w-full gap-2"
              >
                <QrCode className="h-4 w-4" />
                {showQR ? 'Hide' : 'Show'} QR Code
              </Button>

              {showQR && (
                <div className="flex flex-col items-center gap-3 p-4 bg-white rounded-lg border">
                  <QRCodeSVG
                    id="team-qr-code"
                    value={publicUrl}
                    size={200}
                    level="H"
                    includeMargin={true}
                  />
                  <Button
                    onClick={downloadQRCode}
                    variant="secondary"
                    size="sm"
                    className="gap-2"
                  >
                    Download QR Code
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TeamSharingControls;